/**
 * Manages subscription product variant selection based on quantity.
 */
class ScriptSelector {
  constructor(productCardUpdater = null, bundleManager = null) {
    this.scripts = document.querySelectorAll(
      'script[data-script-type="subscription-products-variant-data"]'
    );
    const bundleVersionType = document.querySelector('script[data-script-type="subscription-products-variant-data"]');


    if (bundleVersionType?.getAttribute("data-bundle-type") === "old") {
      this.productTypeMap = {
        2: "buy-1-to-get-13",
        4: "buy-2-to-get-20",
        6: "buy-3-to-get-26",
        8: "buy-4-to-get-30",
      };
    } else if (bundleVersionType?.getAttribute("data-bundle-type") === "new") {
      this.productTypeMap = {
        2: "buy-2-at-19-98",
        4: "buy-4-at-39-98",
        8: "buy-8-at-79-98"
      };
    }
    this.productCardUpdater = productCardUpdater;
    this.bundleManager = bundleManager; // Reference to BundleManager
    this.observeQtyChanges();
  }

  /**
   * Observes changes to quantity attributes on elements.
   */
  observeQtyChanges() {
    const qtyElements = document.querySelectorAll("[data-qty]");
    if (!qtyElements.length) {
      console.warn("No elements with [data-qty] found.");
      return;
    }

    const observer = new MutationObserver(() => {
      const totalQty = this.getTotalQty();
      const selectedScript = this.getScriptByTotalQty(totalQty);

      if (this.productCardUpdater) {
        this.productCardUpdater.updateProductCards();
      }
      if (this.bundleManager) {
        this.bundleManager.totalQty = totalQty; // Update totalQty
        this.bundleManager.updateCheckoutButtonUI(); // Update UI
        this.bundleManager.updateMinusButtonsState(); // Update minus buttons based on total
      }
    });

    qtyElements.forEach((element) => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ["data-qty"],
      });
    });
  }

  /**
   * Calculates total quantity from all [data-qty] elements.
   * @returns {number} Total quantity
   */
  getTotalQty() {
    return Array.from(document.querySelectorAll("[data-qty]")).reduce(
      (total, el) => total + (parseInt(el.getAttribute("data-qty"), 10) || 0),
      0
    );
  }

  /**
   * Finds the appropriate script based on total quantity.
   * @param {number} totalQty - Total quantity
   * @returns {Element|null} Matching script element
   */
  getScriptByTotalQty(totalQty) {

    const sortedKeys = Object.keys(this.productTypeMap)
      .map(Number)
      .sort((a, b) => a - b);
    const matchedKey =
      sortedKeys.find((k) => k >= totalQty) ||
      sortedKeys[sortedKeys.length - 1];
    const targetProductType = this.productTypeMap[matchedKey];
    return (
      Array.from(this.scripts).find(
        (s) => s.getAttribute("data-product-type") === targetProductType
      ) || null
    );
  }

  /**
   * Parses JSON data from a script element.
   * @param {Element} script - Script element
   * @returns {Array} Parsed variants
   */
  getVariantsFromScript(script) {
    if (!script) return [];
    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Invalid JSON in subscription script:", error);
      return [];
    }
  }
}

/**
 * Updates product cards based on selected variants.
 */
class ProductCardUpdater {
  constructor(scriptSelector, cartItems) {
    this.scriptSelector = scriptSelector;
    this.cartItems = cartItems;
    this.MIN_QUANTITY = 1; // Default minimum quantity
    this.calculateDiscount = this.getCalculateDiscountFunction();
  }

  /**
   * Determines the discount calculation function based on bundle handle.
   * @returns {Function} Discount calculation function
   */
  getCalculateDiscountFunction() {
    const bundleVersionType = document.querySelector('script[data-script-type="subscription-products-variant-data"]');
    if (bundleVersionType?.getAttribute("data-bundle-type") === "old") {
      return function (quantity) {
        if (quantity > 8) return 50;
        if (quantity > 4 && quantity <= 8) return 45;
        if (quantity > 2 && quantity <= 4) return 40;
        if (quantity >= 1 && quantity <= 2) return 35;
        return 0;
      };
    } else {
      return function (quantity) {
        if (quantity > 4) return 50;
        if (quantity > 2 && quantity <= 4) return 45;
        if (quantity >= 1 && quantity <= 2) return 40;
        return 0;
      };
    }
  }

  /**
   * Updates product cards with variant data based on total quantity.
   */
  updateProductCards() {
    const productCards = document.querySelectorAll(
      ".product-list .cs_bun_slected_product_main"
    );
    if (!productCards.length) {
      console.warn("No product cards found.");
      return;
    }

    const totalQty = this.scriptSelector.getTotalQty();
    this.updatePercentageOfSavings(totalQty);
    const script = this.scriptSelector.getScriptByTotalQty(totalQty);
    if (!script) {
      console.warn("No matching script found for quantity:", totalQty);
      return;
    }

    const variants = this.scriptSelector.getVariantsFromScript(script);

    productCards.forEach((card, index) => {
      const variant = variants[index];
      if (!variant) return;

      const variantId = variant.id;
      const sellingPlanId =
        variant.selling_plan_allocations?.[0]?.selling_plan_id || null;

      card.setAttribute("data-product-id", script.getAttribute("id") || "");
      card.setAttribute("data-variant-id", variantId);
      if (sellingPlanId) {
        card.setAttribute("data-selling-plan-id", sellingPlanId);
      } else {
        card.removeAttribute("data-selling-plan-id");
      }

      const qty = parseInt(card.getAttribute("data-qty"), 10) || 0;
      if (qty > 0) {
        this.cartItems[variantId] = {
          qty,
          sellingPlanId
        };
      } else {
        delete this.cartItems[variantId];
      }
    });
  }

  /**
   * Updates the discount percentage display.
   * @param {number|null} dynamicTotalQuantity - Total quantity to calculate discount
   */
  updatePercentageOfSavings(dynamicTotalQuantity = null) {
    const urlParams = new URLSearchParams(window.location.search);
    const isVariationB =
      urlParams.get("buy-box-variation")?.toLowerCase() === "b";
    const quantity = dynamicTotalQuantity != null ? dynamicTotalQuantity : this.MIN_QUANTITY;
    if (quantity) {
      const discount = this.calculateDiscount(quantity);
      if (discount > 0) {
        localStorage.setItem("bundle_discount", discount);
        const discountEl = document.querySelector(".discunt-save"); // Fixed typo
        if (discountEl) {
          if (isVariationB) {
            discountEl.textContent = `50%`;
          } else {
            discountEl.textContent = `${discount}%`;
          }
        } else {
          console.warn('Element with class "discunt-save" not found.');
        }
        const label = document.querySelector('label[for="auto_refill"]');
        if (label) {
          if (isVariationB) {
            label.textContent = label.textContent = "Save 50% with Smart Refills! Zero Commitment, Cancel Anytime";
          } else {
            label.textContent = label.textContent.replace(/Save \d+%/, `Save ${discount}%`);
          }
        } else {
          console.warn('Label for "auto_refill" not found.');
        }
      }
    }
  }
}

/**
 * Manages the bundle checkout process and product interactions.
 */
class BundleManager {
  constructor() {
    this.MIN_QUANTITY =
      parseInt(
        new URLSearchParams(window.location.search).get("quantity"),
        10
      ) || 1;
    this.variantId = new URLSearchParams(window.location.search).get(
      "variant_id"
    );
    this.cartItems = {};
    this.footerItems = {};
    this.giftItems = {};
    this.shippingItems = {};
    this.swiper = null;
    this.totalQty = 0;
    this.shippingType = this.getShippingTypeFromURL();
    this.minQuantityThreshold = this.MIN_QUANTITY;
    this.scriptSelector = new ScriptSelector(null, this); // Pass BundleManager reference
    this.productCardUpdater = new ProductCardUpdater(
      this.scriptSelector,
      this.cartItems
    );
    this.scriptSelector.productCardUpdater = this.productCardUpdater;

    this.elements = {
      checkoutLockIcon: document.querySelector(".secure-checkout-icon"),
      checkoutText: document.getElementById("checkout-text"),
      checkoutSpinner: document.getElementById("checkout-spinner"),
      toggle: document.getElementById("toggleShippingProtection"),
      autoRefillCheckbox: document.getElementById("auto_refill"),
      checkoutButton: document.getElementById("add-gifts-btn"),
      warningMessage: document.getElementById("bundle-warning-msg"),
    };
    this.addAnimationOnMysteryGift();
  }

  getShippingTypeFromURL() {
    const params = new URLSearchParams(window.location.search);
    return params.get("shipping-type") || null;
  }

  /**
   * Initializes event listeners and setup.
   */
  initialize() {
    document.addEventListener("DOMContentLoaded", () => {
      this.handleUrlParams();
      this.updatePercentageOfSavings();
      this.setupProductCards();
      this.setupFooterProducts();
      this.initializeSwiperOnUpsells();
      this.productCardUpdater.updateProductCards();
      this.setupCheckout();
      this.autoSelectFirstProduct();
      this.initializeQuantities();

      this.updateShippingProtection();
      this.applyBlueATCStyle();
    });
  }

  /**
   * Returns the total quantity of selected flavors (sum of quantities from all product cards).
   * @returns {number} Total quantity of selected flavors
   */
  getTotalFlavors() {
    return this.totalQty;
    // Alternatively, for fresh calculation:
    // return this.scriptSelector.getTotalQty();
  }

  /**
   * Updates all minus buttons based on total quantity across all flavors.
   * Disables minus buttons when total quantity is <= 2.
   */
  updateMinusButtonsState() {
    const totalQty = this.scriptSelector.getTotalQty();
    const productCards = document.querySelectorAll(".cs_bun_slected_product_main");
    const MINIMUM_TOTAL_QTY = 2;

    productCards.forEach((card) => {
      const minusBtn = card.querySelector(".qty-minus");
      const cardQty = parseInt(card.getAttribute("data-qty"), 10) || 0;

      if (minusBtn) {
        // Disable minus button if total quantity is at minimum (2) or less
        // Only disable if this specific card has quantity > 0
        const shouldDisable = totalQty <= MINIMUM_TOTAL_QTY && cardQty > 0;
        
        minusBtn.disabled = shouldDisable;
        minusBtn.style.opacity = shouldDisable ? "0.5" : "1";
        minusBtn.style.cursor = shouldDisable ? "not-allowed" : "pointer";
        minusBtn.setAttribute("aria-disabled", shouldDisable ? "true" : "false");
      }
    });
  }


  /**
   * Update Shipping product based on the Url if the URl contains recurring shipping then update the shipping product attribute values.
   */

  updateShippingProtection() {
    const shippingBox = document.getElementById("shipping-protection");
    if (!shippingBox) return; // Exit early if element not found

    const hideBox = () => shippingBox.style.display = "none";

    switch (this.shippingType) {
      case "recurring-shipping":
        hideBox();
        Object.assign(shippingBox.dataset, {
          productId: "9968487924003",
          variantId: "54230149890339",
          sellingPlanId: "8154186019"
        });
        break;

      case "no-shipping-product":
        hideBox();
        break;

      default:
        shippingBox.style.display = "flex"; // Revert to CSS default
    }
  }


  /**
   Apply Blue ATC Styles based on the URL Parameter
   */
  applyBlueATCStyle() {
    const params = new URLSearchParams(window.location.search);
    const blue_atc_test = params.get("blue-atc-test") || "false";

    if (blue_atc_test === "true" || blue_atc_test === true) {
      document.querySelector(".cs_bun_img_text_main")?.classList.add("blue-atc-07N25")
    }
  }
  /**
   * Stores total quantity and URL quantity parameter on page load.
   */
  initializeQuantities() {
    this.totalQty = Array.from(document.querySelectorAll("[data-qty]")).reduce(
      (total, el) => total + (parseInt(el.getAttribute("data-qty"), 10) || 0),
      0
    );

    // Update Planted Trees
    const selectedQtyEl = document.querySelector(
      ".cs_cart_footer_heading #selected-qty"
    );
    if (selectedQtyEl) {
      selectedQtyEl.textContent = this.totalQty;
    } else {
      console.warn('Element with id "selected-qty" not found.');
    }
    // const treePlantedEl = document.querySelector(
    //   "[data-product-title='Tree Planted'] + p"
    // );
    // if (treePlantedEl) {
    //   treePlantedEl.textContent = `${this.totalQty} Tree Planted`;
    // } else {
    //   console.warn('Element for "Tree Planted" text not found.');
    // }

    (() => {
      const csCartUrl = window.location.href;
      const csFlavourBundlePages = [
        'new-variation-b-flavours',
        'variation-b-flavours',
        'g-wagon-bundle-flavours'
      ];

      const csIsFlavourBundlePage = csFlavourBundlePages.some(page =>
        csCartUrl.includes(page)
      );

      const csFreeShippingBanner = document.querySelector(
        '.cs-cart-drawer-free-shipping-qualification'
      );

      if (csFreeShippingBanner && csIsFlavourBundlePage) {
        csFreeShippingBanner.classList.toggle(
          'active',
          this.totalQty >= 4
        );
      }
    })();

    this.minQuantityThreshold = this.MIN_QUANTITY;
    this.updateCheckoutButtonUI();
    this.updateMinusButtonsState(); // Initialize minus buttons state based on total quantity
  }

  /**
   * Updates the checkout button and warning message based on totalQty and minQuantityThreshold.
   */
  updateCheckoutButtonUI() {
    if (
      !this.elements.checkoutButton ||
      !this.elements.warningMessage ||
      !this.elements.checkoutText
    ) {
      console.warn(
        "Checkout button, warning message, or checkout text element missing."
      );
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const least_no_of_boxes = params.get("least-no-of-boxes") || "dynamic";

    // Threshold only for warning message text
    const threshold = least_no_of_boxes === "one"
      ? 1
      : least_no_of_boxes === "two"
      ? 2
      : this.MIN_QUANTITY;

    let shouldDisable = false;

    if (least_no_of_boxes === "dynamic") {
      shouldDisable = this.totalQty < this.minQuantityThreshold;
    } else if (least_no_of_boxes === "one") {
      shouldDisable = this.totalQty < 1;
    } else if (least_no_of_boxes === "two") {
      shouldDisable = this.totalQty < 2;
    } else {
      // fallback
      shouldDisable = this.totalQty < this.MIN_QUANTITY;
    }

    if (shouldDisable) {
      // Disable button and show warning
      this.elements.checkoutButton.disabled = true;
      this.elements.warningMessage.classList.add("show-warning");
      this.elements.warningMessage.textContent = `Select at least ${threshold} item${threshold > 1 ? "s" : ""}`;
      // this.elements.warningMessage.style.display = "block";
      this.elements.checkoutText.style.display = "none";
      // this.elements.checkoutLockIcon.style.display= "none";
    } else {
      // Enable button and hide warning
      this.elements.checkoutButton.disabled = false;
      this.elements.warningMessage.classList.remove("show-warning");
      this.elements.warningMessage.textContent = "";
      // this.elements.warningMessage.style.display = "none";
      this.elements.checkoutText.style.display = "block";
      // this.elements.checkoutLockIcon.style.display= "block";
    }
  }



  /**
   * Automatically selects the first product card based on budqty2 value.
   */
  autoSelectFirstProduct() {
    const params = new URLSearchParams(window.location.search);
    
    // Check if quantity parameter is available in URL
    const quantityParam = params.get("quantity");
    let targetValue = null;
    
    if (quantityParam) {
      targetValue = parseInt(quantityParam, 10);
      if (isNaN(targetValue) || targetValue <= 0) {
        console.warn(`Invalid quantity parameter value: ${quantityParam}`);
        targetValue = null; // Fall back to default behavior
      }
    }
    
    // If no valid quantity parameter, use the default logic
    if (targetValue === null) {
      const least_no_of_boxes = params.get("least-no-of-boxes") || "dynamic";

      // pick ID based on param
      const targetId = least_no_of_boxes === "dynamic" ? "budqty2" : "budqty";

      const targetElement = document.getElementById(targetId);
      if (!targetElement) {
        console.warn(`No element with id "${targetId}" found.`);
        return;
      }

      targetValue = parseInt(targetElement.textContent, 10);
      if (isNaN(targetValue) || targetValue <= 0) {
        console.warn(
          `Invalid or non-positive ${targetId} value:`,
          targetElement.textContent
        );
        return;
      }
    }

    const productList = document.querySelector(".product-list");

    if (!productList) {
      console.warn("No element with class .product-list found.");
      return;
    }

    const firstProductCard = productList.querySelector('.cs_bun_slected_product_main');

    if (!firstProductCard) {
      console.warn("No .cs_bun_slected_product_main found in .product-list.");
      return;
    }

    const addBtn = firstProductCard.querySelector(".cs_bun_select_pro_add_butt");
    const qtyPlusBtn = firstProductCard.querySelector(
      ".quantity-selector .qty-plus"
    );
    if (!addBtn || !qtyPlusBtn) {
      console.warn(
        "Missing .cs_bun_select_pro_add_butt or .qty-plus in first product card."
      );
      return;
    }

    // Hide the add button and auto-increase quantity
    addBtn.style.display = "none";
    for (let i = 0; i < targetValue; i++) {
      qtyPlusBtn.click();
    }
  }


  /**
   * Initializes Swiper for upsell products.
   */
  initializeSwiperOnUpsells() {
    if (typeof Swiper === "undefined") {
      console.warn("Swiper library not loaded.");
      return;
    }

    const swiperContainer = document.querySelector(".my-collection-Swiper");
    if (!swiperContainer) {
      console.warn("No element found with class .my-collection-Swiper.");
      return;
    }

    const paginationElement = document.querySelector(".cart_swiper-pagination");
    if (!paginationElement) {
      console.warn(
        "No pagination element found with class .cart_swiper-pagination."
      );
    }

    this.swiper = new Swiper(".my-collection-Swiper", {
      slidesPerView: 1.2,
      spaceBetween: 12,
      speed: 1000,
      initialSlide: 1,
      pagination: {
        el: ".cart_swiper-pagination",
        clickable: true,
      },
    });
  }

  /**
   * Navigates to the previous page after resetting UI.
   */
  goBack() {
    document
      .querySelectorAll(".cs_bun_slected_product_main")
      .forEach((card) => {
        card.dataset.qty = "0";
        const qtyText = card.querySelector(".qty-number");
        const addBtn = card.querySelector(".cs_bun_select_pro_add_butt");
        const qtyBox = card.querySelector(".quantity-selector");
        if (qtyText) qtyText.textContent = "0";
        if (addBtn) addBtn.style.display = "block";
        if (qtyBox) qtyBox.style.display = "none";
      });
    this.cartItems = {};
    this.footerItems = {};
    this.totalQty = 0;
    this.productCardUpdater.updateProductCards();
    this.updateUI();
    this.updateCheckoutButtonUI();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
    // fetch("/cart/clear.js", {
    //   method: "POST"
    // })
    //   .then(() => {
    //     console.log("🧹 Cart cleared before navigating back.");
    //     document
    //       .querySelectorAll(".cs_bun_slected_product_main")
    //       .forEach((card) => {
    //         card.dataset.qty = "0";
    //         const qtyText = card.querySelector(".qty-number");
    //         const addBtn = card.querySelector(".cs_bun_select_pro_add_butt");
    //         const qtyBox = card.querySelector(".quantity-selector");
    //         if (qtyText) qtyText.textContent = "0";
    //         if (addBtn) addBtn.style.display = "block";
    //         if (qtyBox) qtyBox.style.display = "none";
    //       });
    //     this.cartItems = {};
    //     this.footerItems = {};
    //     this.totalQty = 0;
    //     this.productCardUpdater.updateProductCards();
    //     this.updateUI();
    //     this.updateCheckoutButtonUI();
    //     if (this.swiper) {
    //       this.swiper.destroy(true, true);
    //       this.swiper = null;
    //     }
    //     sessionStorage.removeItem("justCheckedOut");
    //     if (window.history.length > 1) {
    //       window.history.back();
    //     } else {
    //       window.location.href = "/";
    //     }
    //   })
    //   .catch((error) => {
    //     console.error("❌ Failed to clear cart before navigating back:", error);
    //     if (this.swiper) {
    //       this.swiper.destroy(true, true);
    //       this.swiper = null;
    //     }
    //     if (window.history.length > 1) {
    //       window.history.back();
    //     } else {
    //       window.location.href = "/";
    //     }
    //   });
  }

  /**
   * Updates UI based on URL parameters.
   */
  handleUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const least_no_of_boxes = params.get("least-no-of-boxes") || "dynamic";
    const boxOrBoxes = document.querySelector(".box-or-boxes");
    const autoRefill = params.get("auto-refill");

    // Uncheck auto_refill checkbox if auto-refill is 'false'
    if (autoRefill === "false") {
      const autoRefillCheckbox = document.getElementById("auto_refill");
      if (autoRefillCheckbox) {
        autoRefillCheckbox.checked = false;
      } else {
        console.warn('No checkbox found with id "auto_refill"');
      }
    }

    if (this.MIN_QUANTITY > 0) {
      const quantityMap = {
        dynamic: this.MIN_QUANTITY,
        one: 1,
        two: 2
      };

      const threshold = quantityMap[least_no_of_boxes] ?? this.MIN_QUANTITY;  
      const plural = threshold > 1 ? "boxes" : "box";

      // set text
      if (boxOrBoxes) boxOrBoxes.textContent = plural;

      ["budqty", "budqty2", "selected-qty"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = threshold;
      });
    }

    // if (this.MIN_QUANTITY > 0) {
    //   ["budqty", "budqty2", "selected-qty"].forEach((id) => {
    //     const el = document.getElementById(id);
    //     if (el) el.textContent = this.MIN_QUANTITY;
    //   });
    // }
  }

  /**
   * Updates the discount percentage by delegating to ProductCardUpdater.
   * @param {number|null} dynamicTotalQuantity - Total quantity to calculate discount
   */
  updatePercentageOfSavings(dynamicTotalQuantity = null) {
    this.productCardUpdater.updatePercentageOfSavings(dynamicTotalQuantity);
  }

  /**
   * Sets up event listeners for product card interactions.
   */
  setupProductCards() {
    const productCards = document.querySelectorAll(
      ".cs_bun_slected_product_main"
    );
    if (!productCards.length) {
      console.warn("No product cards found for setup.");
      return;
    }

    productCards.forEach((card) => {
      const addBtn = card.querySelector(".cs_bun_select_pro_add_butt");
      const qtyBox = card.querySelector(".quantity-selector");
      const minusBtn = card.querySelector(".qty-minus");
      const plusBtn = card.querySelector(".qty-plus");
      const qtyText = card.querySelector(".qty-number");
      const enteriesContainer = card.querySelector("[data-calculated-value]");

      const updateCard = (qty = 0) => {
        qty = Math.max(0, qty);
        card.dataset.qty = qty;
        if (qtyText) qtyText.textContent = qty;
        if (addBtn) addBtn.style.display = qty > 0 ? "none" : "block";
        if (qtyBox) qtyBox.style.display = qty > 0 ? "flex" : "none";
        this.productCardUpdater.updateProductCards();

        const variantId = card.getAttribute("data-variant-id");
        const sellingPlanId = card.getAttribute("data-selling-plan-id");
        const baseValue = parseInt(10);

        if (qty > 0 && variantId) {
          this.cartItems[variantId] = {
            qty,
            sellingPlanId
          };
        } else if (variantId) {
          delete this.cartItems[variantId];
        }

        // Update calculated entries safely
        if (enteriesContainer && baseValue) {
          const calculatedValue = baseValue * qty;
          enteriesContainer.textContent = calculatedValue > 0 ? calculatedValue : 10;
        }

        this.initializeQuantities();
        this.updateGiftStatus();
        this.updateMinusButtonsState(); // Update minus buttons based on total quantity
      };

      addBtn?.addEventListener("click", () => updateCard(1));
      plusBtn?.addEventListener("click", () =>
        updateCard((parseInt(card.dataset.qty, 10) || 0) + 1)
      );
      minusBtn?.addEventListener("click", () => {
        const currentQty = parseInt(card.dataset.qty, 10) || 0;
        const totalQty = this.scriptSelector.getTotalQty();
        
        // Prevent decreasing if total would go below 2
        if (totalQty > 2) {
          updateCard(currentQty - 1);
        }
      });
    });
  }

  /**
   * Sets up event listeners for footer product buttons.
   */
  setupFooterProducts() {
    const footerButtons = document.querySelectorAll(".add-footer-product");
    if (!footerButtons.length) {
      console.warn("No footer product buttons found.");
      return;
    }

    footerButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const variantId = btn.dataset.variantId;
        if (!variantId) {
          console.warn("No data-variant-id found on footer product button.");
          return;
        }

        const isActive = btn.classList.contains("active-footer-product");

        footerButtons.forEach((b) => {
          b.classList.remove("active-footer-product");
          b.textContent = `+ ${b.dataset.price || ""}`;
        });

        if (!isActive) {
          btn.classList.add("active-footer-product");
          btn.textContent = "✔ Added";
          this.footerItems[variantId] = {
            qty: 1,
            sellingPlanId: null
          };
          this.totalQty += 1; // Increment totalQty
        } else {
          delete this.footerItems[variantId];
          this.totalQty -= 1; // Decrement totalQty
        }

        this.updateUI();
        this.updateCheckoutButtonUI();
      });
    });
  }

  /**
   * Updates the UI for footer products and other elements.
   */
  updateUI() {
    document.querySelectorAll(".add-footer-product").forEach((btn) => {
      const variantId = btn.dataset.variantId;
      if (this.footerItems[variantId]) {
        btn.classList.add("active-footer-product");
        btn.textContent = "✔ Added";
      } else {
        btn.classList.remove("active-footer-product");
        btn.textContent = `+ ${btn.dataset.price || ""}`;
      }
    });
  }

  /**
   * Sets up the checkout button event listener.
   */
  setupCheckout() {
    if (!this.elements.checkoutButton) {
      console.warn("Checkout button not found.");
      return;
    }
    
    // Ensure button type is set to prevent form submission
    if (this.elements.checkoutButton.type === "submit" || !this.elements.checkoutButton.type) {
      this.elements.checkoutButton.type = "button";
    }
    
    this.elements.checkoutButton.addEventListener("click", (e) => {
      this.checkoutHandler(e);
    }, { capture: true }); // Use capture phase to catch event early
  }

  /**
   * Prepares the payload for cart items, including footer products.
   * @returns {Array} Array of cart items
   */
  prepareCartItemsPayload() {
    const productCards = document.querySelectorAll(
      ".cs_bun_slected_product_main"
    );
    const items = [];

    const autoRefillCheckbox = document.querySelector(
      ".auto-refill input[type=checkbox]"
    );
    const isAutoRefilled = autoRefillCheckbox ?
      autoRefillCheckbox.checked :
      false;

    productCards.forEach((card) => {
      const variantId = card.getAttribute("data-variant-id");
      const sellingPlanId = card.getAttribute("data-selling-plan-id");
      const qty = parseInt(card.getAttribute("data-qty"), 10) || 0;

      if (qty > 0 && variantId) {
        const item = {
          id: variantId,
          quantity: qty
        };
        if (sellingPlanId && isAutoRefilled) item.selling_plan = sellingPlanId;
        items.push(item);
      }
    });

    Object.entries(this.footerItems).forEach(([variantId, {
      qty
    }]) => {
      if (qty > 0 && variantId) {
        items.push({
          id: variantId,
          quantity: qty
        });
      }
    });

    return items;
  }

  /**
   * Prepares gift items for the cart.
   * @returns {Array} Array of gift items
   */
  prepareGiftItems() {

    const giftItems = [];
    const currentGiftBlock = document.querySelector(".cstm-gift-items.block");
    const lockedGiftsData = currentGiftBlock?.getAttribute("data-gifts-locked");

    const variant_id_Ebook = 50930857869603;
    const variant_id_money_back_guarantee = 52513487978787;

    if (!currentGiftBlock) return giftItems;

    const giftVariants = currentGiftBlock.querySelectorAll(".cstn-gift-img-title:not(.buy-box-gift-card-locked)");
    const totalQty = this.prepareCartItemsPayload().reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    giftVariants.forEach((item, index) => {
      const variantId = item.getAttribute("data-variant-id");
      const giftHandle = item.getAttribute("data-gift-handle");

      // ✅ Skip if gift card is locked
      if (item.classList.contains("buy-box-gift-card-locked")) return;

      // ✅ Skip mystery gift if total quantity is less than 4
      if (giftHandle === "free-mystery-gift" && totalQty < 4) return;

      // Skip invalid gifts (no variant ID)
      if (!variantId) return;

      // if (totalQty <= 3 && lockedGiftsData === "free-shipping-and-mystary" && giftHandle === "free-mystery-gift") return;

      // All gifts should have quantity 1, not matching parent product quantity
      const qty = 1;
      giftItems.push({
        id: variantId,
        quantity: qty
      });
    });
    return giftItems;
  }

  /**
   * Prepares shipping protection items for the cart.
   * @returns {Array} Array of shipping items
   */
  prepareShippingItems() {
    this.updateShippingProtection();

    const shippingEl = document.getElementById("shipping-protection");
    const shippingVariantId = shippingEl?.dataset?.variantId || null;
    const sellingPlanId = shippingEl?.dataset?.sellingPlanId || null;

    // Case 1: Recurring shipping
    if (this.shippingType === "recurring-shipping") {
      if (this.elements.toggle?.checked && shippingVariantId) {
        const item = {
          id: shippingVariantId,
          quantity: 1
        };
        if (sellingPlanId) {
          item.selling_plan = sellingPlanId;
        }
        return [item];
      }
      return [];
    }

    // Case 2: No shipping
    if (this.shippingType === "no-shipping-product") {
      return [];
    }

    // Case 3: Any other shipping type
    if (this.elements.toggle?.checked && shippingVariantId) {
      return [{
        id: shippingVariantId,
        quantity: 1
      }];
    }

    return [];
  }


  /**
   * Handles the checkout process.
   * @param {Event} e - Click event
   */
  async checkoutHandler(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    this.updateCheckoutButtonState(true);

    // Ensure gift status is updated before preparing gift items
    this.updateGiftStatus();

    setTimeout(async () => {
      const productItems = this.prepareCartItemsPayload();

      const isUSD =
        window.shopifySettings &&
        typeof window.shopifySettings.moneyFormat === "string" &&
        window.shopifySettings.moneyFormat.includes("$");
      // const giftItems = isUSD ? this.prepareGiftItems() : [];
      const giftItems = this.prepareGiftItems();
      const shippingItems = this.prepareShippingItems();

      const itemsToAdd = [...productItems, ...giftItems, ...shippingItems];

      if (itemsToAdd.length === 0) {
        console.warn("⚠️ No items selected.");
        this.updateCheckoutButtonState(false);
        return;
      }


      const discountCode = `FreeShippingFGBY19MXG4T7FREE46571316579812365457`;
      // const checkoutWithDiscountedURL = `/checkout?discount=${discountCode}`
      const checkoutWithDiscountedURL = `/checkout`;
      // total quantity across all productItems
      const totalQty = productItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

      const checkoutURL = totalQty >= 4 ? checkoutWithDiscountedURL : `/checkout`;

      if (totalQty >= 8) {
        try {
          const cartResponse = await fetch("/cart.js");
          if (!cartResponse.ok) throw new Error("Failed to fetch cart");
          const cartData = await cartResponse.json();

          //const giftVariantId = 54453756657955;
          const alreadyInCart = cartData.items.some(
            (item) => item.variant_id === giftVariantId
          );

          if (!alreadyInCart) {
            //itemsToAdd.push({ id: giftVariantId, quantity: 1 });
            console.log("🎁 Gift item added to checkout payload");
          } else {
            console.log("✅ Gift item already in cart, skipping add");
          }
        } catch (error) {
          console.error("🚫 Error checking existing cart items:", error);
        }
      }


      this.processCheckout(itemsToAdd, checkoutURL);
    }, 2000);
  }

  /**
   * Displays a toast notification.
   * @param {string} message - Message to display
   * @param {string} type - Type of toast (e.g., "error", "success")
   */
  showToast(message, type = "error") {
    const oldToast = document.querySelector(".custom-toast");
    if (oldToast) oldToast.remove();

    const toast = document.createElement("div");
    toast.className = `custom-toast ${type}`;
    toast.textContent = message; // Use textContent for safety

    Object.assign(toast.style, {
      display: "none",
      position: "fixed",
      color: "#fff",
      padding: "12px 20px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      fontSize: "14px",
      zIndex: 9999,
      opacity: 0,
      transition: "opacity 0.3s ease",
      background: type === "error" ? "#f44336" : "#4CAF50",
    });

    if (window.innerWidth <= 750) {
      Object.assign(toast.style, {
        left: "15px",
        right: "15px",
        bottom: "100px",
        textAlign: "center",
      });
    } else {
      toast.style.right = "30px";
      toast.style.bottom = "100px";
    }

    document.body.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
      toast.style.display = "block";
      toast.style.opacity = 1;
    });

    setTimeout(() => {
      toast.style.opacity = 0;
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Processes the checkout by clearing and adding items to the cart.
   * @param {Array} itemsToAdd - Items to add to the cart
   */
  async processCheckout(itemsToAdd, checkoutURL) {
    // clearCartIfContainsTargetVariants(targetVariants);
    try {
      // Clear cart before adding new items
      const clearRes = await fetch("/cart/clear.js", {
        method: "POST"
      });
      if (!clearRes.ok) throw new Error("❌ Failed to clear cart");

      const addRes = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Request-ID": `custom-checkout-${Date.now()}-${Math.random()}`,
        },
        body: JSON.stringify({
          items: itemsToAdd
        }),
      });

      if (!addRes.ok) {
        const errorData = await addRes.json();
        throw new Error(errorData.message || "❌ Failed to add items to cart");
      }

      this.updateCheckoutButtonState(false);
      window.location.href = `${checkoutURL}`;
    } catch (error) {
      console.error("🚫 Checkout failed:", error);
      this.updateCheckoutButtonState(false);
      this.showToast(error.message || "Unexpected error occurred", "error");
    }
  }

  /**
   * Updates the checkout button's UI state.
   * @param {boolean} isLoading - Whether the button is in loading state
   */
  updateCheckoutButtonState(isLoading) {
    const arrow = document.querySelector(".cstm-btn-arrow");
    if (isLoading) {
      if (this.elements.checkoutText)
        this.elements.checkoutText.style.display = "none";
      // this.elements.checkoutLockIcon.style.display= "none";
      if (this.elements.checkoutSpinner)
        this.elements.checkoutSpinner.classList.remove("hidden");
      if (arrow) arrow.style.display = "none";
      if (this.elements.warningMessage)
        this.elements.warningMessage.style.display = "none";
    } else {
      if (this.elements.checkoutText)
        this.elements.checkoutText.style.display = "block";
      if (this.elements.checkoutSpinner)
        this.elements.checkoutSpinner.classList.add("hidden");
      if (arrow) arrow.style.display = "block";
    }
  }

  addAnimationOnMysteryGift() {
    (function () {
      const MYSTERY_GIFT_NS = 'myShopifyMysteryGift_';

      function initMysteryGiftAnimations() {
        // Silently skip animation if Lottie is not available
        if (typeof lottie === 'undefined') {
          return;
        }

        const giftCardClasses = ['.buy-box-gift-card-mystry-gift', '.buy-box-gift-card-premium', '.buy-box-gift-card-premium-plus'];

        giftCardClasses.forEach((className) => {
          const giftCards = document.querySelectorAll(className);
          if (!giftCards.length) {
            console.warn(`⚠️ No gift cards found for ${className}`);
            return;
          }

          giftCards.forEach((card) => {
            const giftUnlock = card.querySelector('.unlock-animated-icon');
            if (!giftUnlock) {
              console.warn(`⚠️ .unlock-animated-icon not found inside ${className}`);
              return;
            }

            try {
              const giftAnim = lottie.loadAnimation({
                container: giftUnlock,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                path: 'https://cdn.shopify.com/s/files/1/0904/3869/7251/files/spring.json?v=1759738041'
              });

              giftAnim.addEventListener('DOMLoaded', () => {
                console.log(`✅ Lottie loaded successfully for ${className}`);
              });

              giftAnim.addEventListener('data_failed', () => {
                console.error(`❌ Failed to load Lottie animation data for ${className}`);
              });
            } catch (error) {
              console.error(`❌ Error initializing Lottie animation for ${className}:`, error);
            }
          });
        });
      }

      function setupMysteryGiftAnimations() {
        document.addEventListener('DOMContentLoaded', initMysteryGiftAnimations);
      }

      window[MYSTERY_GIFT_NS + 'setupAnimations'] = setupMysteryGiftAnimations;
      window[MYSTERY_GIFT_NS + 'setupAnimations']();
    })();
  }

  updateGiftStatus() {
    const freeShippingBar = document.querySelector('[data-free-shipping-simple-text-icon]');
    document.querySelectorAll('.buy-box-gift-card-mystry-gift')?.forEach(e => {
      const wasLocked = e.classList.contains('buy-box-gift-card-locked'); // check old state

      // Toggle locked state based on qty
      if (e.classList.contains('buy-box-gift-card-premium-plus')) {
        e.classList.toggle('buy-box-gift-card-locked', this.totalQty < 8);
      } else {
        e.classList.toggle('buy-box-gift-card-locked', this.totalQty <= 3);
      }

      const isNowLocked = e.classList.contains('buy-box-gift-card-locked');

      // If it was locked but now unlocked → trigger the temporary icon animation class
      if (wasLocked && !isNowLocked) {
        console.log('🔓 Mystery Gift unlocked — enabling unlock icon temporarily');

        // Add the "enable" class
        e.classList.add('enable-unlocked--icon');

        // Remove it after 3s and replace with disabled
        setTimeout(() => {
          e.classList.remove('enable-unlocked--icon');
          e.classList.add('disable-unlocked--icon');
        }, 3000);
      }
    });

    if (freeShippingBar) { freeShippingBar.classList.toggle('hide', this.totalQty <= 3) };

    document.querySelectorAll('.buy-box-gift-card-premium')?.forEach(e => {
      // Check the gift product title from data attribute or text content
      const giftCard = e.querySelector('.cstm-gift-card');
      const productTitle = giftCard?.getAttribute('data-product-title') || '';
      const giftTitle = e.querySelector('.cstm-gift-title')?.textContent?.trim() || '';
      const giftHandle = e.getAttribute('data-gift-handle') || '';
      
      // Tongue Scrapper and Free Shipping unlock at 4+, others unlock at 8+
      const unlocksAt4 = productTitle === 'Tongue Scrapper' || 
                        productTitle === 'Free Shipping' ||
                        giftTitle === 'Tongue Scrapper' ||
                        giftHandle === 'free-shipping';
      
      const shouldBeLocked = unlocksAt4 
        ? this.totalQty <= 3  // Locked when qty <= 3, unlocked at 4+
        : this.totalQty <= 7; // Locked when qty <= 7, unlocked at 8+
      
      e?.classList.toggle('buy-box-gift-card-locked', shouldBeLocked);
    });

    document.querySelectorAll('.buy-box-gift-card-premium-plus')?.forEach(e => {
      e?.classList.toggle('buy-box-gift-card-locked', this.totalQty < 8);
    });

    if (this.totalQty >= 8) {
      document.querySelector("#budqty").textContent = "6"; // All 6 gifts unlocked
    } else if (this.totalQty >= 4) {
      document.querySelector("#budqty").textContent = "4"; // 4 gifts unlocked
    } else {
      document.querySelector("#budqty").textContent = "2"; // Default: 2 gifts unlocked
    }
  }
}

// Initialize the bundle manager
const bundleManager = new BundleManager();
bundleManager.initialize();
